import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import appliedPromotionsSelector from 'selectors/basket/appliedPromotions/appliedPromotionsSelector';
import { personalizationSelector } from 'viewModel/selectors/personalization/personalizationSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import { dynamicComponentSelector } from 'viewModel/selectors/dynamicComponent/dynamicComponentSelector';
import { p13nSelector } from 'selectors/p13n/p13nSelector';
import {
    isMabDynamicEndDateList, readMabBoPromoEndDate, getMabBoPromoEndDate, applyMabDynamicEndDate
} from 'utils/mabDynamicEndDate';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import PromotionListEN from 'components/Content/PromotionList/locales/PromotionList_en_US';
import PromotionListFR from 'components/Content/PromotionList/locales/PromotionList_fr_CA';
import Empty from 'constants/empty';
import PersonalizationUtils from 'utils/Personalization';
import { buildTrendingOffersSotFields } from 'analytics/utils/trendingOfferTracking';

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PromotionListEN, PromotionListFR);

const fields = createSelector(
    coreUserDataSelector,
    personalizationSelector,
    appliedPromotionsSelector,
    dynamicComponentSelector,
    p13nSelector,
    (_state, ownProps) => ownProps.items,
    (_state, ownProps) => ownProps.showSkeleton,
    (_state, ownProps) => ownProps.features,
    (_state, ownProps) => ownProps.personalization,
    createStructuredSelector({
        viewAll: getTextFromResource(getText, 'viewAll')
    }),
    (user, personalization, appliedPromotions, dynamicComponentData, p13n, items, showSkeleton, features, componentPersonalization, localization) => {
        const { personalizedComponent, isPersonalizationInitializing } = personalization;
        const { dynamicComponent, isDynamicComponentInitializing } = dynamicComponentData;

        const dynamicItems = dynamicComponent?.items;
        const personalizedItems = personalizedComponent?.variationData?.items;
        const clientRequestId = personalizedComponent?.p13n?.['client_request_id'] || null;

        const isSkeleton = showSkeleton || (user.isInitialized && isPersonalizationInitializing) || isDynamicComponentInitializing;

        // we need to check on priority for personalizedItems vs dynamicItems
        const newItems = personalizedItems || dynamicItems || items || Empty.Array;

        // PDE-2789: for a MAB dynamic-end-date list, override the Contentful endDate with the
        // backend-provided mabBoPromoEndDate. The handler may live on the component or on individual
        // items, so applyMabDynamicEndDate scopes the override accordingly. When the date is absent
        // the items are left untouched so the list keeps its Contentful-configured end date.
        //
        // PME-2113: read the date off `personalizedComponent` first. On a reload the personalization
        // selector resolves the component from the localStorage p13n cache (headData is only
        // populated by the head script on the first load), and that cached entry carries
        // mabBoPromoEndDate while the redux slice no longer does. Scanning the slice alone made the
        // override no-op on every load after the first, so the tile fell back to the Contentful
        // endDate and its countdown flag disappeared.
        const mabBoPromoEndDate = isMabDynamicEndDateList(features, newItems)
            ? readMabBoPromoEndDate(personalizedComponent) || getMabBoPromoEndDate(p13n, componentPersonalization?.context)
            : undefined;
        const resolvedItems = applyMabDynamicEndDate(newItems, mabBoPromoEndDate, features);

        const showPersonalizationOverlay = PersonalizationUtils.shouldShowPersonalizationOverlay(componentPersonalization?.isNBOEnabled);

        const trendingOffersSotFields = buildTrendingOffersSotFields(componentPersonalization);

        return {
            user,
            localization,
            appliedPromotions,
            isPersonalizationInitializing,
            showPersonalizationOverlay,
            items: resolvedItems,
            showSkeleton: isSkeleton,
            areItemsPersonalized: (personalizedItems || Empty.Array)?.length !== 0,
            isNBOEnabled: componentPersonalization?.isNBOEnabled,
            ...(trendingOffersSotFields && { extraSotFields: trendingOffersSotFields }),
            ...(clientRequestId && { clientRequestId })
        };
    }
);

const withPromotionListProps = wrapHOC(connect(fields));

export {
    fields, withPromotionListProps
};
